<template>
  <vx-card class="mt-base">
    <heading size="h2" style-size="h4" class="mb-5">{{ activityTitle }}</heading>
    <p class="mb-5">
      To complete this activity, you will need to be familiar with the information in the Learning
      and Resources section on Conditioning.
    </p>
    <p class="mb-5">
      <a
        href="https://www.psychologytoday.com/au/contributors/susan-krauss-whitbourne-phd"
        target="_blank"
        >Susan Krauss Whitbourne</a
      >
      provided a quiz and states that it will help you polish up your mastery of the terminology as
      well as give you insight into some of the behavioural factors that motivate you in your
      everyday life.
    </p>
    <p class="mb-5">
      See if you can answer these questions about common situations involving learning through
      conditioning.
    </p>

    <div v-for="(question, index) in questions" :key="index">
      <div class="vx-row my-5">
        <div
          class="vx-col w-full mb-2"
          v-if="isEditable"
          :class="{
            wrong:
              answers['question' + index] > 0 && question.correct != answers['question' + index],
          }"
        >
          <label for="">
            <heading size="h3" style-size="h5" class="questionlabel"
              >{{ question.question }}
            </heading>
            <select
              class="default-select"
              v-model="answers['question' + index]"
              :name="'question_' + index"
            >
              <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
                {{ answer.answer }}
              </option>
            </select>
          </label>
          <p
            v-if="
              answers['question' + index] > 0 && question.correct != answers['question' + index]
            "
          >
            Answer: {{ question.explanation }}
          </p>
        </div>
        <div class="my-2 mx-5" v-else>
          <div v-if="answers['question' + index] != 0 && answers['question' + index] != null">
            <p class="my-2" v-for="answer in question.answers" :key="answer.val">
              <span v-if="answers['question' + index] == answer.val">{{ answer.answer }}</span>
            </p>
          </div>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videoTrigger1: require('@/assets/images/experiences/hairdressing/hair_structure_splash.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/hairdressing/hair_structure.mp4',
      },
      login_type: localStorage.getItem('login_type'),
      questions: {
        1: {
          question:
            '1. You hear a song on the radio and suddenly you feel sad; you realise it was popular when you were dating your ex.  What is the conditioned stimulus?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'The song' },
            { val: 2, answer: 'Your ex.' },
          ],
          correct: 1,
          explanation: 'The song (because it is associated with sadness at losing your ex)',
        },
        2: {
          question:
            ' 2. After listening to the constant pestering of your roommate to clean up your desk, you finally clean it up just to make the pestering go away. What type of reinforcement does this situation involve?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Positive' },
            { val: 2, answer: 'Negative' },
          ],
          correct: 2,
          explanation:
            'Negative (because by cleaning up your desk, you made the aversive stimulus go away)',
        },
        3: {
          question:
            ' 3. While passing through an intersection where you were nearly run over by a car, you feel a slight twinge of anxiety. What is the conditioned response?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Anger at the car' },
            { val: 2, answer: 'Your twinge of anxiety' },
          ],
          correct: 2,
          explanation:
            'Your twinge of anxiety (because it is now associated with being in that intersection)',
        },
        4: {
          question:
            '4. You’re standing in the kitchen and start to open a can of pet food. Your pet instantly shows up, whimpering to get fed. What type of stimulus is the sound of the can being opened?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Conditioned' },
            { val: 2, answer: 'Unconditioned' },
          ],
          correct: 1,
          explanation: 'Conditioned (because it is associated with the food)',
        },
        5: {
          question:
            '5. Smelling diesel fuel when you’re around boats makes you feel ill and seasick because on your last boat trip, you became violently ill. What was the experience of being violently ill when it first occurred?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Conditioned' },
            { val: 2, answer: 'Unconditioned' },
          ],
          correct: 2,
          explanation:
            'Unconditioned response (because no learning took place when you were first ill; later it became a conditioned response)',
        },
        6: {
          question:
            '6. You used to enjoy working out, but now you’ve stopped because you dropped a weight on your foot on your last trip to the gym. What was the weight in this situation?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Punishment' },
            { val: 2, answer: 'Reinforcement' },
          ],
          correct: 1,
          explanation: 'Punishment (because it made you stop going to the gym)',
        },
        7: {
          question:
            '7. You’re trying to quit smoking, and so when you go for 4 hours without smoking, you allow yourself to play 5 minutes of your favourite video game. What is the video game?',
          answers: [
            { val: 0, answer: 'Please Select' },
            { val: 1, answer: 'Positive Reinforcement' },
            { val: 2, answer: 'Negative Punishment' },
          ],
          correct: 1,
          explanation: 'Positive reinforcement (because it’s a reward for not smoking)',
        },
      },
      answers: {
        question1: 0,
        question2: 0,
        question3: 0,
        question4: 0,
        question5: 0,
        question6: 0,
        question7: 0,
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.data.question1 !== 'undefined') {
      this.answers = this.data;
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}

.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
</style>
